import React, { useRef, useEffect, useContext, useImperativeHandle, createContext, useState, useMemo, useCallback } from 'react'
import { RENDER_SDK, RESOURCE_MANAGER } from '@/config'
import { RendererContext } from '@/components/FURenderkit'
import AvatarController from '../AvatarController'
import AvatarSelect from '../Common/AvatarSelect'
import { TALK_INS } from '../AvatarController/TalkSkill'
import { ModifiedAvatar } from '@/packages/sdk/fu_render_kit/lib/RenderKitSDK'
import { SceneContext } from './Scene'
import { IAvatarConfig } from '@/packages/types'
import AvatarRecorder from '../Common/AvatarRecorder'
import { Spin } from 'antd'
import LocalAnimations from "@/assets/AppAssets/animation/AnimationConfig.json"

const ua = navigator.userAgent.toLowerCase()
const agents = ['iphone', 'ipad', 'ipod', 'android', 'linux', 'windows phone']
export const isPC = agents.every(item => {
    return ua.indexOf(item) === -1
})

let width
let height
let originWidth
let originHeight

if (window.innerWidth) {
    width = window.innerWidth
    height = window.innerHeight
} else if (document.body && document.body.clientWidth) {
    width = document.body.clientWidth
    height = document.body.clientHeight
}

// 通过深入 Document 内部对 body 进行检测，获取窗口大小
if (
    document.documentElement &&
    document.documentElement.clientHeight &&
    document.documentElement.clientWidth
) {
    originWidth = document.documentElement.clientWidth
    originHeight = document.documentElement.clientHeight
}

export const AvatarContext = createContext<{
    scheduleUpdateAvatar: (avatarConfig: any) => void
    inTalkSessionRef: { current: boolean }
    avatar?: ModifiedAvatar
    avatarConfig?: IAvatarConfig
    setAvatarConfig: React.Dispatch<React.SetStateAction<IAvatarConfig | undefined>>
}>({
    scheduleUpdateAvatar: () => { },
    inTalkSessionRef: { current: false },
    setAvatarConfig: () => { },
})

const Avatar = React.forwardRef((props: any, ref) => {
    const { renderer, comp, setLoading } = useContext(RendererContext)
    const { scene } = useContext(SceneContext)
    const [avatar, setAvatar] = useState<ModifiedAvatar | undefined>()
    const [avatarConfig, setAvatarConfig] = useState<IAvatarConfig | undefined>()
    const avatarRef = useRef(avatar)
    const gender = useMemo(() => avatarConfig?.data.gender || '', [avatarConfig])
    const caseGender = useMemo(() => gender.slice(0, 1).toUpperCase() + gender.slice(1).toLowerCase(), [gender])
    const [loadingAvatar, setLoadingAvatar] = useState(false)

    // avatar update后
    useEffect(() => {
        (async () => {
            if (!avatar) return
            avatarRef.current = avatar
            // 在safari中为保证帧率 需要关闭阴影
            // avatar.setAllCastShadow(false);

            // 关闭物理效果 获得更好性能
            // avatar.setDynamicBoneAllEnable(0);

            await avatar.setAnimationAsync(
                `GAssets/animation/Asia${caseGender}/common/ani_ptag_a${gender}_huxi.bundle`
            );

            avatarInited()
            initAvatarRotate()
        })()

    }, [avatar])

    // 拖拽旋转
    const touchstartListener = useRef<any>(null)
    const touchmoveListener = useRef<any>(null)
    const touchendListener = useRef<any>(null)
    function initAvatarRotate() {
        let originX = 0
        let originRotation = 0
        const canvas = document.getElementById("canvas")
        const startEventName = isPC ? "mousedown" : "touchstart"
        const moveEventName = isPC ? "mousemove" : "touchmove"
        const endEventName = isPC ? "mouseup" : "touchend"
        if (!canvas) return
        if (touchstartListener.current) {
            canvas.removeEventListener(startEventName, touchstartListener.current)
            canvas.removeEventListener(moveEventName, touchmoveListener.current)
            canvas.removeEventListener(endEventName, touchendListener.current)
        }
        touchstartListener.current = (event) => {
            originX = event.touches ? event.touches[0].pageX : event.pageX
            if (avatar) {
                originRotation = avatar.getRotate()[1]
            }
            canvas.addEventListener(moveEventName, touchmoveListener.current)
            canvas.addEventListener(endEventName, touchendListener.current)
        }

        touchmoveListener.current = (event) => {
            let currentX = event.touches ? event.touches[0].pageX : event.pageX
            let x = currentX - originX
            if (avatar) {
                avatar.setRotate([0, originRotation + (x / 150) * 180, 0])
            }
        }

        touchendListener.current = () => {
            canvas.removeEventListener(moveEventName, touchmoveListener.current)
            canvas.removeEventListener(endEventName, touchendListener.current)
        }

        canvas.addEventListener(startEventName, touchstartListener.current)
    }

    function avatarInit() {
        //创建 默认avatar
        scheduleUpdateAvatar(RESOURCE_MANAGER.defaultAvatar)
    }
    // 修改avatar
    async function scheduleUpdateAvatar(avatarConfig) {
        stopRenderLoop()
        setLoading(true)
        // UI更新优先
        setTimeout(async () => {
            if (avatar) {
                destroyAvatar(avatar)
            }
            const newAvatar = await scene?.customAddAvatarAsync(
                avatarConfig
            )
            setAvatarConfig({ ...avatarConfig })
            setAvatar(newAvatar)
        }, 20)
    }
    // 注销avatar
    function destroyAvatar(avatar) {
        scene?.removeAvatar(avatar)
    }

    // avatar 形象变更结束
    function avatarInited() {
        setLoading(false)
        startRenderLoop()
    }
    const previousFrameTimestamp = useRef(0)
    const lastStatisticsTime = useRef(0)
    // 开始渲染
    function startRenderLoop() {
        previousFrameTimestamp.current = performance.now()
        console.log('开始渲染时间', previousFrameTimestamp.current)

        // 设置帧数开始统一时间
        lastStatisticsTime.current = previousFrameTimestamp.current
        tick(performance.now())
        setLoadingAvatar(false)
    }

    // 渲染动画id
    const animationId = useRef<number | undefined>(0)

    // 停止渲染
    function stopRenderLoop() {
        if (animationId.current) {
            window.cancelAnimationFrame(animationId.current)
            animationId.current = undefined
        }
    }

    // 渲染过程
    const tick = (timestamp) => {
        animationId.current = window.requestAnimationFrame(tick)
        draw(timestamp)
    }
    // 绘制每一帧
    function draw(timestamp) {
        const elapsed = timestamp - previousFrameTimestamp.current
        if (elapsed >= 16) {
            previousFrameTimestamp.current = timestamp
            update()
            renderer?.render(scene, elapsed / 1000)
        }
    }
    // 帧数
    function showFPS(timestamp) {
        const timeGap = timestamp - lastStatisticsTime.current
        if (timeGap > 1000) {
            lastStatisticsTime.current = timestamp
        }
    }

    // 嘴部动作
    const inTalkSessionRef = useRef(false)
    const update = (flag = 1) => {
        if (avatarRef.current && inTalkSessionRef.current) {
            const exp = TALK_INS.getCurrentExpression(flag);
            avatarRef.current?.setAvatarMouthBlendShape(exp);
        }
    }

    useImperativeHandle(ref, () => ({
        avatarInit,
    }))

    // 点击事件
    async function onClickHandler(e) {
        if (!scene || !avatar) return
        const collisionPoint = scene.getCollisionPoint(e)
        if (collisionPoint) {
            const index = Math.floor(Math.random() * 3)
            const path = LocalAnimations[gender][index].path
            await RENDER_SDK._resourceManager?.prepareCustomBundle(
                path,
                path
            )
            avatar.setAnimationSyncOnce(
                path,
                `GAssets/animation/Asia${caseGender}/common/ani_ptag_a${gender}_huxi.bundle`
            )
        }
    }

    return (
        <div className='avatar'>
            <AvatarContext.Provider
                value={
                    {
                        avatar,
                        scheduleUpdateAvatar,
                        inTalkSessionRef,
                        avatarConfig,
                        setAvatarConfig,
                    }
                }
            >
                <div
                    id="fu-renderkit-container"
                    style={{
                        display: 'block'
                    }}
                >
                    <Spin spinning={loadingAvatar}>
                        <canvas id="canvas" onClick={onClickHandler}></canvas>
                    </Spin>
                    {
                        (comp === 'dressup' || comp === 'material') && (
                            <>
                                <AvatarRecorder />
                                <AvatarSelect />
                            </>
                        )
                    }
                </div>
                <AvatarController />
            </AvatarContext.Provider>
        </div>
    )
})

export default Avatar
